import { HttpInterceptorFn, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { OfflineStateService } from '../offline/services/offline-state.service';
import { OfflineRouteMapper } from '../offline/services/offline-route-mapper';
import { PendingQueueService } from '../offline/services/pending-queue.service';

/**
 * Offline interceptor — ONLY active when `environment.isTauri && !navigator.onLine`.
 *
 * - GET requests: served from RxDB via OfflineRouteMapper
 * - POST/PUT/PATCH/DELETE: queued in PendingQueueService for later sync
 * - Assets and public endpoints: passed through (handled upstream by authInterceptor)
 */
export const offlineInterceptor: HttpInterceptorFn = (req, next) => {
  // Only activate in Tauri desktop mode
  if (!(environment as Record<string, unknown>)['isTauri']) {
    return next(req);
  }

  // If online, pass through
  if (navigator.onLine) {
    return next(req);
  }

  // Skip local assets (i18n, images) — let them through to the cache/disk
  if (req.url.startsWith('./assets/') || req.url.startsWith('/assets/') || req.url.startsWith('assets/')) {
    return next(req);
  }

  const offlineState = inject(OfflineStateService);
  const routeMapper = inject(OfflineRouteMapper);
  const pendingQueue = inject(PendingQueueService);

  if (req.method === 'GET') {
    return handleGetOffline(req.url, offlineState, routeMapper);
  }

  // Mutations: queue for later sync
  return handleMutationOffline(req, pendingQueue);
};

function handleGetOffline(
  url: string,
  offlineState: OfflineStateService,
  routeMapper: OfflineRouteMapper,
): Observable<HttpResponse<unknown>> {
  const mapping = routeMapper.resolve(url);
  if (!mapping) {
    // No mapping found — return 503 (service unavailable offline)
    return throwError(() => new HttpErrorResponse({
      status: 503,
      statusText: 'Offline — no local data available for this route',
      url,
    }));
  }

  const db = offlineState.getDb();
  if (!db) {
    return throwError(() => new HttpErrorResponse({
      status: 503,
      statusText: 'Offline DB not initialized',
      url,
    }));
  }

  // Special case: POS init combines multiple collections
  if (mapping.collection === '__pos_init__') {
    return new Observable(observer => {
      Promise.all([
        db.products.find({ selector: { isActive: true } }).exec(),
        db.stocks.find().exec(),
        db.pricing_rules.find({ selector: { isActive: true } }).exec(),
        db.tax_rules.find({ selector: { isActive: true } }).exec(),
      ]).then(([products, stocks, pricingRules, taxRules]) => {
        observer.next(new HttpResponse({
          status: 200,
          body: {
            products: products.map((d: { toJSON: () => unknown }) => d.toJSON()),
            stocks: stocks.map((d: { toJSON: () => unknown }) => d.toJSON()),
            pricingRules: pricingRules.map((d: { toJSON: () => unknown }) => d.toJSON()),
            taxRules: taxRules.map((d: { toJSON: () => unknown }) => d.toJSON()),
          },
        }));
        observer.complete();
      }).catch(err => {
        observer.error(new HttpErrorResponse({
          status: 500,
          statusText: `RxDB error: ${err?.message || err}`,
          url,
        }));
      });
    });
  }

  const collection = (db.collections as Record<string, unknown>)[mapping.collection] as {
    find: (opts?: { selector?: Record<string, unknown> }) => { exec: () => Promise<Array<{ toJSON: () => unknown }>> };
    findOne: (id: string) => { exec: () => Promise<{ toJSON: () => unknown } | null> };
  } | undefined;

  if (!collection) {
    return throwError(() => new HttpErrorResponse({
      status: 503,
      statusText: `Collection "${mapping.collection}" not available offline`,
      url,
    }));
  }

  return new Observable(observer => {
    const query = mapping.idParam
      ? collection.findOne(mapping.idParam).exec().then(doc => doc ? [doc] : [])
      : collection.find({ selector: mapping.selector }).exec();

    query.then(docs => {
      const data = docs.map(d => d.toJSON());
      const body = mapping.idParam ? (data[0] ?? null) : data;
      observer.next(new HttpResponse({ status: 200, body }));
      observer.complete();
    }).catch(err => {
      observer.error(new HttpErrorResponse({
        status: 500,
        statusText: `RxDB query error: ${err?.message || err}`,
        url,
      }));
    });
  });
}

function handleMutationOffline(
  req: Parameters<HttpInterceptorFn>[0],
  pendingQueue: PendingQueueService,
): Observable<HttpResponse<unknown>> {
  // Extract headers relevant for replay
  const replayHeaders: Record<string, string> = {};
  for (const key of ['x-company-id', 'x-warehouse-id', 'x-user-email', 'x-user-name', 'content-type']) {
    const val = req.headers.get(key);
    if (val) replayHeaders[key] = val;
  }

  let body: unknown = null;
  try {
    body = req.body;
  } catch {
    // body might not be serializable
  }

  const entry = pendingQueue.enqueue(req.method, req.url, body, replayHeaders);

  // Return a synthetic 202 Accepted response
  return of(new HttpResponse({
    status: 202,
    body: {
      offline: true,
      queued: true,
      pendingId: entry.id,
    },
  }));
}
